import React, {SetStateAction, useContext, useState} from 'react';
import {ClassroomController, ConnectionFactory} from "../../../backends/YiiRoutes";
import {observer} from "mobx-react";
import ClickableLink from "../../utilities/ClickableLink";
import {IClassroom} from "./UserSettings";
import Popup from "../../utilities/Popup";
import NotificationManager from "../../notifications/NotificationManager";
import styles from "./PersonModal.module.scss";
import Button from "../../utilities/Button";
import FontAwesome from "../../utilities/FontAwesome";
import {useMachine} from "@xstate/react/lib";
import {FetchMachine} from "../../machines/FetchMachine";
import {StoreContext} from "../../../stores/StoreLoader";

function EditPopup(props: {
    type: "new-classblocks" | "new-page" | "edit",
    title?: string,
    url?: string,
    teacherId: string,
    classroomId?: string,
    setClassrooms: SetStateAction<any>,
    close: () => void,
}) {
    const [title, setTitle] = useState(props.title || "");
    const [url, setUrl] = useState(props.url || "");

    const [current, send] = useMachine(FetchMachine);

    const {i18nStore, organizationStore} = useContext(StoreContext);

    async function handleSave() {
        send("FETCH");
        try {
            const externalClassroomUrl = (props.type !== "new-classblocks" ? url : undefined);
            const q = {
                id: props.type === "edit" ? props.classroomId : "0",
                title,
                teacher: props.teacherId,
                externalClassroomUrl,
            }

            const requestUrl = props.type === "edit" ? ClassroomController.updateExternalClassroomLink(i18nStore.currentOrgSupportedLocale) : ClassroomController.create(i18nStore.currentOrgSupportedLocale);

            const data: {saved?: boolean, title: string, classroomUrl?: string, id: string, isExternalClassroom?: boolean} = await ConnectionFactory(organizationStore.currentOrganization.id, requestUrl, {
                method: "POST",
                data: {
                    Classroom: q,
                },
            }
            )

            if (data.saved === true) {
                send("FULFILL");
                props.close();
                NotificationManager.success(`Classroom ${props.type === "edit" ? "updated" : "created"} successfully.`);

                if (props.type === "edit") {
                    props.setClassrooms(v => v.map(c => {
                        if (c.id === props.classroomId) {
                            return {
                                ...c,
                                title: data.title,
                                classroomUrl: data.classroomUrl,
                            };
                        }
                        return c;
                    }));
                } else {
                    props.setClassrooms(v => v.concat([{id: data.id, title: data.title, classroomUrl: data.classroomUrl, isExternalClassroom: data.isExternalClassroom}]));
                }
            } else {
                send("REJECT");
                NotificationManager.error(`There was an error ${props.type === "edit" ? "updating" : "creating"} your classroom.  Please try again`);
            }
        } catch (e) {
            send("REJECT");
            NotificationManager.error(`There was an error ${props.type === "edit" ? "updating" : "creating"} your classroom.  Please try again`);
        }
    }

    async function handleDelete() {
        if (props.classroomId) {
            send("FETCH");

            try {
                const data: {deleted?: boolean} = await ConnectionFactory(
                    organizationStore.currentOrganization.id,
                    ClassroomController.delete(i18nStore.currentOrgSupportedLocale, props.classroomId),
                    {
                        method: 'POST',
                        cache: false,
                    }
                )
                if (data.deleted === true) {
                    send("FULFILL");
                    props.close();
                    props.setClassrooms(v => v.filter(c => c.id !== props.classroomId));
                    NotificationManager.success("Classroom deleted successfully.");
                } else {
                    send("REJECT");
                    NotificationManager.error("There was an error deleting your classroom.  Please try again");
                }
            } catch (e) {
                send("REJECT");
                NotificationManager.error("There was an error deleting your classroom.  Please try again");
            }

        }
    }

    return <div className={styles.editPopup}>
        <Button key={"exit"} onClick={props.close}>
            <FontAwesome prefix={'fas'} name={'fa-times'}/>
        </Button>
        <h3>{props.type === "new-classblocks" ? "Set the ClassBlocks Page title" : "Edit or delete the title and URL"}</h3>
        {current.value === "PENDING" ? <div style={{textAlign: "center"}}>
            <FontAwesome spin size={"lg"} prefix={'fas'} name={"fa-spinner"} />
        </div> : <div>
            <div key={"title"}>
                <label htmlFor={"title"}>Title: </label>
                <input id={"title"} value={title} onChange={e => setTitle(e.target.value)}/>
            </div>
            {props.type === "new-classblocks" || <div key={"url"}>
                <label htmlFor={"url"}>URL: </label>
                <input id={"url"} value={url} onChange={e => setUrl(e.target.value)}/>
            </div>}
            <div key={"buttons"}>
                <Button key={"save"} onClick={handleSave}>
                    <FontAwesome prefix={'fas'} name={'fa-check'}/>
                </Button>
                {props.type === "edit" && <Button key={"delete"} onClick={handleDelete}>
                    Delete
                </Button>}
            </div>
        </div>}
    </div>
}

export const ClassroomButton = observer((props: {
    classroom?: IClassroom,
    setClassrooms: React.SetStateAction<any>,
    teacherId: string,
    type: "new-classblocks" | "new-page" | "edit",
    canEdit: boolean,
}) => {
    const [popupOpen, setPopupOpen] = useState(false);

    let content;
    if (props.classroom) {
        let linkContent;
        switch (props.classroom.linkedClassroomType) {
            case "google":
                linkContent = <span className={styles.linkToSite}>
                    <img src={require("../../../assets/images/google_classroom_green.png")} style={{width: "1.2em"}}/>
                </span>;
                break;
            case "schoology":
                linkContent = <span className={styles.linkToSite}>
                    <img src={require("../../../assets/images/schoology_logo_small.png")} style={{width: "1.2em"}}/>
                </span>;
                break;
        }

        if (props.classroom.isExternalClassroom) {
            content = <>
                <ClickableLink href={props.classroom.classroomUrl}>
                    <div className={styles.classroomButton}>
                        {props.classroom.title}
                    </div>
                </ClickableLink>
                {props.canEdit && <>
                    <FontAwesome
                    onClick={() => setPopupOpen(true)}
                    name={'fa-link'}
                    prefix={'fas'}
                    size={'lg'}
                    className={styles.linkToSite}
                    title="Edit or delete the title and URL"/>
                    <Popup editable={props.canEdit}
                           open={popupOpen}
                           setOpen={setPopupOpen}
                           renderPopup={<EditPopup
                                type={props.type}
                                close={() => setPopupOpen(false)}
                                teacherId={props.teacherId}
                                classroomId={props.classroom.id}
                                setClassrooms={props.setClassrooms}
                                title={props.classroom.title}
                                url={props.classroom.classroomUrl}
                            />}
                           arrowPosition={"bottom"}>
                    </Popup>
                </>}
            </>
        } else {
            content = <ClickableLink href={props.classroom.classroomUrl}
                                     className={`${styles.classroomButton} ${props.classroom.isLinkedClassroomSuggestion ? styles.newClassroomButton : ""}`}>
                {props.classroom.title}
                {linkContent}
            </ClickableLink>
        }
    } else {
        let label;
        if (props.type === "new-classblocks") {
            label = <span
                className={`${styles.classroomButton} ${styles.newClassroomButton}`}
                title="Set the ClassBlocks Page title">
                <span key="a" className={styles.spanCreate}>Create New</span> <span
                key="b" className={styles.spanCreateNew}>ClassBlocks</span> <span key="c"
                                                                                  className={styles.spanCreateNew}>Page</span>
            </span>
        } else if (props.type === "new-page") {
            label = <span className={`${styles.classroomButton} ${styles.newClassroomButton}`}
                          title="Edit or delete the title and URL">
                <span key="a" className={styles.spanCreate}>Create New</span> <span
                key="b" className={styles.spanCreateNew}>Link to a Site</span>
            </span>
        }

        content = <Popup editable={true}
                         open={popupOpen}
                         setOpen={setPopupOpen}
                         renderPopup={<EditPopup type={props.type} close={() => setPopupOpen(false)}
                                                 teacherId={props.teacherId}
                                                 setClassrooms={props.setClassrooms}/>}
                         arrowPosition={"bottom"}>
            {label}
        </Popup>
    }

    return <span className={styles.classroomButtonContainer}>
        {content}
    </span>
})

